import type { AdminSelf } from '@saay/api-client';
import { DataState, EmptyState, type DataStateProps, Skeleton } from '../kit.js';
import { useLogin } from '@saay/web-app';
import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router';

import { useRegisterPageTitle } from './admin-shell.js';
import { type AdminArea, canSeeArea } from '../permissions.js';
import { useAdminSelf } from '../queries.js';

/** The document title; a list under an open route sheet passes `active = false` (the sheet names it). */
export function usePageTitle(title: string, active = true, barTitle: string = title): void {
  // The top app bar shows the page title (owner W4 decision); a detail page shows its kind there
  // («خریدار») and the record's name in its own sticky header.
  useRegisterPageTitle(barTitle, active);
  useEffect(() => {
    if (active) {
      document.title = `${title} — پنل مدیریت SAAY`;
    }
  }, [active, title]);
}

/**
 * Renders a screen only for roles that read its area (D-31). The self context decides the gating;
 * while it loads the screen shows a skeleton, and a failed load shows its own state.
 */
export function RequireArea({
  area,
  children,
}: {
  readonly area: AdminArea;
  readonly children: (self: AdminSelf) => ReactNode;
}) {
  const self = useAdminSelf();
  const login = useLogin();
  if (self.status === 'pending') {
    return <Skeleton label="در حال بارگذاری…" lines={4} />;
  }
  if (self.status === 'error') {
    return (
      <DataState
        error={self.error}
        onLogin={() => login()}
        onRetry={() => void self.refetch()}
        session
        status="error"
      >
        {null}
      </DataState>
    );
  }
  if (!canSeeArea(self.data, area)) {
    return (
      <EmptyState
        action={<Link to="/">رفتن به صفحهٔ اصلی</Link>}
        icon="shield"
        title="به این بخش دسترسی ندارید"
      />
    );
  }
  return <>{children(self.data)}</>;
}

/** `DataState` with the session handlers wired (re-login returns to the same route). */
export function QueryState(
  props: Omit<DataStateProps, 'onLogin'> & { readonly onRetry?: () => void },
) {
  const login = useLogin();
  return <DataState {...props} onLogin={() => login()} />;
}
